import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import Svg, { ClipPath, Defs, Ellipse, G, Path, Rect } from 'react-native-svg';
import { createStatusChartViewStyles } from './StatusChartView.styles';
import { useScreenTheme } from '@shared/hooks/useScreenTheme';
import { TooltipPosition } from './StatusChartViewProps';
import PieChartArrow from '@shared/assets/icons/PieChartArrow';

/**
 * @interface statusChartViewProps
 * @description Defines the props for the StatusChartView component.
 * @property {Array<{ title: string; count: number; color: string }>} statusChartsList - A list of objects representing each status chart item.
 */
interface StatusChartViewProps {
  statusChartsList: { title: string; count: number; color: string }[];
}

/**
 * @component StatusChartView
 * @description A component that renders a donut chart with interactive tooltips to display status information.
 * @param {StatusChartViewProps} props - The props for the component.
 */
const StatusChartView = ({ statusChartsList }: StatusChartViewProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | null>(null);
  const theme = useScreenTheme('DashboardScreen');
  const styles = useMemo(() => createStatusChartViewStyles(theme), [theme]);

  // Calculate the total count of all items
  const total = statusChartsList.reduce((acc, curr) => acc + curr.count, 0);

  // Chart dimensions and properties
  const cx = 110;
  const cy = 109.5;
  const outerR = 100;
  const innerR = 60;
  const cornerR = 6; // Radius for the corners

  /**
   * @function getRoundedPath
   * @description Generates the SVG path data for a rounded donut chart slice.
   * @param {number} startAngle - The starting angle of the slice.
   * @param {number} endAngle - The ending angle of the slice.
   * @returns {string} The SVG path data string.
   */
  const getRoundedPath = (startAngle: number, endAngle: number) => {
    const toRad = (a: number) => ((a - 90) * Math.PI) / 180;

    // Convert corner radius to angular offsets for both outer and inner arcs
    const outerOffset = (cornerR / outerR) * (180 / Math.PI);
    const innerOffset = (cornerR / innerR) * (180 / Math.PI);

    const sO = startAngle + outerOffset;
    const eO = endAngle - outerOffset;
    const sI = startAngle + innerOffset;
    const eI = endAngle - innerOffset;

    if (eO <= sO) return '';

    const getP = (r: number, a: number) => ({
      x: cx + r * Math.cos(toRad(a)),
      y: cy + r * Math.sin(toRad(a)),
    });

    // 8 points defining the arcs and the rounded corners
    const p1 = getP(outerR, sO);
    const p2 = getP(outerR, eO);
    const p3 = getP(outerR - cornerR, endAngle);
    const p4 = getP(innerR + cornerR, endAngle);
    const p5 = getP(innerR, eI);
    const p6 = getP(innerR, sI);
    const p7 = getP(innerR + cornerR, startAngle);
    const p8 = getP(outerR - cornerR, startAngle);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${p1.x} ${p1.y}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${p2.x} ${p2.y}`, // Outer Arc
      `Q ${getP(outerR, endAngle).x} ${getP(outerR, endAngle).y} ${p3.x} ${
        p3.y
      }`, // Top-Right Corner
      `L ${p4.x} ${p4.y}`, // Radial Side Line
      `Q ${getP(innerR, endAngle).x} ${getP(innerR, endAngle).y} ${p5.x} ${
        p5.y
      }`, // Bottom-Right Corner
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${p6.x} ${p6.y}`, // Inner Arc
      `Q ${getP(innerR, startAngle).x} ${getP(innerR, startAngle).y} ${p7.x} ${
        p7.y
      }`, // Bottom-Left Corner
      `L ${p8.x} ${p8.y}`, // Radial Side Line
      `Q ${getP(outerR, startAngle).x} ${getP(outerR, startAngle).y} ${p1.x} ${
        p1.y
      }`, // Top-Left Corner
      'Z',
    ].join(' ');
  };

  let currentAngle = 0;
  const sliceGap = 5;

  // Calculate angles for each slice
  let startAngle = 0;
  const angles = statusChartsList.map(item => {
    if (item?.count > 0) {
      const sliceAngle = (item?.count / total) * 360;
      const angleRange = { start: startAngle, end: startAngle + sliceAngle };
      startAngle += sliceAngle;
      return angleRange;
    }
  });

  // Calculate tooltip position based on the selected slice
  let tooltipPosition: TooltipPosition | null = null;
  if (selected !== null) {
    const sliceAngle = angles[selected];
    if (sliceAngle) {
      const midAngle = (sliceAngle.start + sliceAngle.end - sliceGap) / 2;
      const rad = ((midAngle - 90) * Math.PI) / 180;
      const tooltipRadius = outerR;

      const x = cx + tooltipRadius * Math.cos(rad);
      const y = cy + tooltipRadius * Math.sin(rad);

      tooltipPosition = {
        left: x,
        top: y,
      };
    }
  }

  // Text to display in the tooltip
  const centerText =
    selected === null
      ? ''
      : `${t(statusChartsList[selected].title)} (${
          statusChartsList[selected].count
        })`;

  return (
    <View style={styles.container}>
      <View style={styles.chartWrapper}>
        <Svg width="220" height="219" viewBox="0 0 220 219">
          <Ellipse cx={cx} cy={cy} rx="110" ry="109.5" fill="white" />
          <Path
            d="M110 0.5C170.477 0.5 219.5 49.3031 219.5 109.5C219.5 169.697 170.477 218.5 110 218.5C49.5227 218.5 0.5 169.697 0.5 109.5C0.5 49.3031 49.5227 0.5 110 0.5Z"
            stroke="black"
            strokeOpacity={0.05}
            fill="none"
          />
          <G clipPath="url(#clip0_313_794)">
            {statusChartsList
              .filter(item => item.count > 0)
              .map((item, index) => {
                const sweep = total === 0 ? 0 : (item.count / total) * 360;
                const d = getRoundedPath(
                  currentAngle,
                  currentAngle + sweep - sliceGap,
                );
                currentAngle += sweep;
                return (
                  <Path
                    key={item.color}
                    testID={`slice-${index}`}
                    onPress={() =>
                      setSelected(selected === index ? null : index)
                    }
                    d={d}
                    fill={theme?.[item.color as keyof typeof theme]}
                  />
                );
              })}
          </G>
          <Defs>
            <ClipPath id="clip0_313_794">
              <Rect
                width="200"
                height="200"
                fill="white"
                transform="translate(10 9.5)"
              />
            </ClipPath>
          </Defs>
        </Svg>

        {/* Tooltip */}
        {selected !== null && tooltipPosition && (
          <View
            style={[
              styles.tooltipContainer,
              {
                position: 'absolute',
                left: tooltipPosition.left,
                top: tooltipPosition.top,
                transform: [
                  { translateX: -50 }, // Center tooltip horizontally
                  { translateY: -40 }, // Position tooltip above the slice
                ],
              },
            ]}
          >
            <View style={styles.tooltipBox}>
              <Text style={styles.tooltipText}>{centerText}</Text>
            </View>
            <View style={styles.arrowDown}>
              <PieChartArrow />
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default StatusChartView;
